/*
 * SVD product-card analytics — click, impression + variant-select, built off the native `SvdNativeProduct`.
 *
 * Engines-reuse (see docs/svd-product-card/ssr-implementation-plan.md → "Card parity"): the heavy
 * machinery is shared, id-driven infra — the telemetry sinks (`trackEvent`), the batched-analytics
 * stream, Algolia, finding-method, recommendation-tab-data. We reuse those engines and build an
 * SVD-native payload here, rather than reusing the legacy `ProductCardLink` / capture hook (both
 * hard-typed on `IProductCard`/`CardVariant`, which would reintroduce the legacy↔SVD type coupling
 * Option 2 removed). The wire-event / click-context field names mirror the legacy card so the
 * downstream `InternalAnalytics` bus + `getProductDataForBatchedAnalytics` consume them unchanged.
 *
 * Not yet covered (follow-ups): EDD fields (no wire field — Tier-3 BFF ask), is_final_sale (rides on
 * the deferred promo-pricing work).
 */
import { useEffect, useRef, type RefObject } from 'react'

import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import {
  getColorSwatchPosition,
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import { handleAlgoliaClickEvent } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { batchedAnalytics, BATCHED_EVENT_SORT_NODE } from '@src/quince-site-common/src/utils/batched-analytics'
import {
  runAfterInteractionPaint,
  runWhenMainThreadIdle,
} from '@src/quince-site-common/src/utils/interaction-scheduling'
import { setFindingMethod } from '@src/telemetry/findingMethod'
import type { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import {
  clearCurrentRecommendationTabData,
  recommendationTabDataFromTrackContext,
  setCurrentRecommendationTabData,
} from '@src/telemetry/recommendation-tab-data'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

/** Tag values as analytics tag keys — the shape `getIndividualTagStatus` / `transformTags…` expect. */
const toTagKeys = (applicableTags: ProductCardTag[]): ProductTagKey[] =>
  applicableTags.map(tag => tag.value as ProductTagKey)

/**
 * SVD-native `productProperties` blob. Anchor-generic (the analytics schema field is named `color`
 * for historical reasons; for a non-color anchor it carries the anchor value, e.g. a size). Mirrors
 * `getProductPropertiesForTrackingForCard` field-for-field on the native SVD shape.
 */
export function buildSvdProductProperties(
  product: SvdNativeProduct,
  selectedVariant: SvdNativeCardVariant | undefined,
  tagKeys: ProductTagKey[],
  trackConfig?: TrackConfig,
): ProductProperties & { product_position?: number } {
  return {
    product_id: product.relatedIds.spid.toString(),
    title: product.title,
    color: selectedVariant?.displayConfig?.displayName,
    count_color_variant: product.colorVariants.length,
    rating: product.reviews.averageRating.toString(),
    tags: tagKeys,
    tag_ids: transformTagsIntoAnalyticsTags(tagKeys),
    review_count: product.reviews.reviewCount,
    oos_flag: getIndividualTagStatus(tagKeys).sold_out,
    division: product.classification?.division ?? '',
    division_id: product.classification?.divisionId.toString() ?? '',
    department: product.classification?.department ?? '',
    department_id: product.classification?.departmentId.toString() ?? '',
    subdepartment: product.classification?.subdepartment ?? '',
    subdepartment_id: product.classification?.subdepartmentId.toString() ?? '',
    price: selectedVariant?.price.salePrice,
    product_position: trackConfig?.context?.product_position as number | undefined,
  }
}

/**
 * The `PRODUCT_CLICK` payload for an SVD card click, or `undefined` when the section passed no
 * track config (no click tracking). Field names + nesting mirror the legacy `ProductCardLink`.
 */
export function buildSvdProductClickPayload({
  product,
  selectedVariant,
  applicableTags,
  trackConfig,
}: {
  product: SvdNativeProduct
  selectedVariant: SvdNativeCardVariant | undefined
  applicableTags: ProductCardTag[]
  trackConfig?: TrackConfig
}) {
  if (!trackConfig) return undefined

  const tagKeys = toTagKeys(applicableTags)
  const anchorValue = selectedVariant?.displayConfig?.value
  const colorVariants = product.colorVariants

  return {
    category: trackConfig.category,
    action: trackConfig.action,
    label: trackConfig.label,
    value: trackConfig.value,
    context: {
      ...trackConfig.context,
      product_id: product.relatedIds.spid,
      color: anchorValue,
      color_swatch_position: getColorSwatchPosition(anchorValue, colorVariants),
      contentContainerType: trackConfig.context?.contentContainerType || CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
      name: product.title,
      color_variant: colorVariants,
      count_color_variant: colorVariants.length,
      rating: product.reviews.averageRating,
      product_position: trackConfig.context?.product_position,
      price: selectedVariant?.price.salePrice,
      tags: tagKeys,
      tag_ids: transformTagsIntoAnalyticsTags(tagKeys),
      ...getIndividualTagStatus(tagKeys),
      productProperties: buildSvdProductProperties(product, selectedVariant, tagKeys, trackConfig),
      // EDD + is_final_sale ride on later PRs (no EDD wire field yet; final-sale on promo-pricing).
      is_final_sale: 0,
      is_app_exclusive: selectedVariant?.appExclusive ? 1 : 0,
    },
  }
}

/**
 * The batched `BULK_PRODUCT_CARD_VIEW` impression event for one SVD card. Keys mirror the legacy
 * new-card capture hook so `getProductDataForBatchedAnalytics` maps it unchanged. `sortNode` is the
 * card's wrapper element — the stream sorts events into DOM order before firing.
 */
export function buildSvdProductViewEvent({
  product,
  selectedVariant,
  applicableTags,
  sortNode,
}: {
  product: SvdNativeProduct
  selectedVariant: SvdNativeCardVariant | undefined
  applicableTags: ProductCardTag[]
  sortNode: Node | null
}) {
  const tagKeys = toTagKeys(applicableTags)
  const anchorConfig = selectedVariant?.displayConfig

  return {
    [BATCHED_EVENT_SORT_NODE]: sortNode,
    productId: product.productId,
    color: anchorConfig?.value,
    productOptions: anchorConfig ? [{ name: anchorConfig.name, value: anchorConfig.value }] : [],
    tags: tagKeys,
    tag_ids: transformTagsIntoAnalyticsTags(tagKeys),
    oos_flag: getIndividualTagStatus(tagKeys).sold_out,
    name: product.title,
    color_variant: product.colorVariants,
    count_color_variant: product.colorVariants.length,
    rating: product.reviews.averageRating,
    ...getIndividualTagStatus(tagKeys),
    spid: product.relatedIds.spid,
    variant_id: selectedVariant?.variantId,
    price: selectedVariant?.price.salePrice,
    productProperties: buildSvdProductProperties(product, selectedVariant, tagKeys),
    is_app_exclusive: selectedVariant?.appExclusive ? 1 : 0,
    is_final_sale: 0,
  }
}

/**
 * Fires the click side-effects for an SVD card, mirroring the legacy `ProductCardLink` onClick:
 * parent callback → finding-method + recommendation-tab attribution (sync) → Algolia + `PRODUCT_CLICK`
 * (deferred past interaction paint). Algolia only fires for search surfaces that supply an objectID.
 */
export function fireSvdProductClick({
  product,
  selectedVariant,
  applicableTags,
  trackConfig,
  onClickCb,
  productSearchObjectId,
  productPosition,
  searchAlgoliaQueryId,
}: {
  product: SvdNativeProduct
  selectedVariant: SvdNativeCardVariant | undefined
  applicableTags: ProductCardTag[]
  trackConfig?: TrackConfig
  onClickCb?: () => void
  productSearchObjectId?: string
  productPosition?: number
  searchAlgoliaQueryId?: string
}) {
  onClickCb?.()

  const context = trackConfig?.context
  if (trackConfig) {
    setFindingMethod[context?.findingMethod as string]?.({
      elementName: (context?.contentContainerName as string) || '',
      elementId: (context?.trackingId as string) || '',
    })
    const recTab = recommendationTabDataFromTrackContext(context)
    if (Object.keys(recTab).length > 0) {
      setCurrentRecommendationTabData(recTab)
    } else {
      clearCurrentRecommendationTabData()
    }
  }

  runAfterInteractionPaint(() => {
    if (productSearchObjectId) {
      handleAlgoliaClickEvent({
        objectID: productSearchObjectId,
        position: productPosition,
        queryID: searchAlgoliaQueryId ?? '',
      })
    }
    const payload = buildSvdProductClickPayload({ product, selectedVariant, applicableTags, trackConfig })
    if (payload) {
      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, payload)
    }
  })
}

/**
 * The `VARIANT_SELECT` payload for an SVD swatch change. Mirrors the legacy `ProductCardFooter` call
 * but anchor-generic: `product_option_name` is the real anchor (`displayConfig.name` — colour, size,
 * …) rather than the legacy hardcoded 'Color'. Transition-shaped like legacy — the `from` variant
 * supplies `internalVariantIds`/`variantIds`/`product_options`, the `to` variant the rest.
 */
export function buildSvdVariantSelectPayload({
  product,
  previousVariant,
  nextVariant,
  trackConfig,
}: {
  product: SvdNativeProduct
  previousVariant: SvdNativeCardVariant | undefined
  nextVariant: SvdNativeCardVariant
  trackConfig?: TrackConfig
}) {
  const context = trackConfig?.context
  const nextTags = nextVariant.tags.map(tag => tag.value)
  const swatchable = product.cardVariants.filter(variant => variant.displayConfig !== null)
  const positionIdx = swatchable.findIndex(variant => variant.variantId === nextVariant.variantId)

  return {
    color: nextVariant.displayConfig?.value,
    product_id: product.relatedIds.spid,
    internalVariantIds: previousVariant?.variantIds,
    variantIds: previousVariant?.svids,
    product_options: previousVariant?.displayConfig
      ? [{ name: previousVariant.displayConfig.name, value: previousVariant.displayConfig.value }]
      : [],
    tags: nextTags,
    tag_ids: transformTagsIntoAnalyticsTags(nextTags),
    stock_info: null,
    entity_id: product.productId,
    entity_type: 'product_id',
    // SVD has no productTypeName on the wire — productCategory is the closest equivalent.
    productType: product.productCategory ?? undefined,
    productTitle: product.title,
    variantTitle: nextVariant.title,
    price: nextVariant.price.salePrice,
    is_product_card: true,
    division: product.classification?.division,
    department: product.classification?.department,
    subdepartment: product.classification?.subdepartment,
    // Anchor-generic — the real anchor name, not the legacy hardcoded 'Color'.
    product_option_name: nextVariant.displayConfig?.name,
    product_option_value: nextVariant.displayConfig?.value,
    element_name: (context?.contentContainerName as string) || null,
    element_display_name: (context?.contentContainerDisplayName as string) || null,
    element_type: (context?.contentContainerType as string) || null,
    element_id: (context?.trackingId as string) || null,
    element_position: (context?.contentContainerPosition as number) || null,
    context: {
      productProperties: buildSvdProductProperties(product, previousVariant, nextTags as ProductTagKey[]),
      product_option_value_position: positionIdx >= 0 ? positionIdx + 1 : undefined,
      autoselect: false,
    },
  }
}

/**
 * Fires `VARIANT_SELECT` when the shopper picks a swatch. Gated on the section supplying a track
 * config (consistent with the click path); the card still updates the selected variant regardless.
 */
export function fireSvdVariantSelect(args: {
  product: SvdNativeProduct
  previousVariant: SvdNativeCardVariant | undefined
  nextVariant: SvdNativeCardVariant
  trackConfig?: TrackConfig
}) {
  if (!args.trackConfig) return
  trackEvent(TELEMETRY_EVENT_NAMES.VARIANT_SELECT, buildSvdVariantSelectPayload(args))
}

/**
 * Pushes the card's impression event to the batched-analytics stream once it scrolls into view.
 * The container (section) owns `initializeBatchedAnalyticsEvent` for `productViewTracker`; this only
 * produces. Fires at most once (guarded), matching the legacy capture hook.
 */
export function useSvdProductCardImpression({
  productViewTracker,
  wrapperRef,
  isVisible,
  product,
  selectedVariant,
  applicableTags,
}: {
  productViewTracker?: string
  wrapperRef: RefObject<HTMLElement | null>
  isVisible: boolean
  product: SvdNativeProduct
  selectedVariant: SvdNativeCardVariant | undefined
  applicableTags: ProductCardTag[]
}) {
  const hasTrackedRef = useRef(false)

  useEffect(() => {
    if (productViewTracker && wrapperRef.current && isVisible && selectedVariant && !hasTrackedRef.current) {
      hasTrackedRef.current = true
      // hasTrackedRef guards re-scheduling, so this is intentionally fire-and-forget.
      runWhenMainThreadIdle(() => {
        batchedAnalytics.pushEventToBatchedAnalyticsStream({
          tracker: productViewTracker,
          event: buildSvdProductViewEvent({
            product,
            selectedVariant,
            applicableTags,
            sortNode: wrapperRef.current,
          }),
          retryCount: 3,
        })
      })
    }
  }, [productViewTracker, wrapperRef, isVisible, product, selectedVariant, applicableTags])
}
